/* ============================================================
   工位看板（气体）· 真实接口数据层
   ------------------------------------------------------------
   「工位总览（StationViewGas）」与「工位详情（StationInfoGas）」共用本模块，
   页面口径（区 / 行 / 桌 / 工位编号 / 产品字段）由这里统一接后端：
   - 实时数据：getStationListSse（GET api/dash/cockpit/station/sse，SSE 长连接）
     打开页面即订阅，后端每 5 秒推一次本区最新工位信息 → openStationStream()
   - 首帧兜底：getStationList（GET api/dash/cockpit/station）
     入参 processCode：'A' → A 区全部工位；空字符串 / 不传 → 全部工位 → fetchBoard()
     返回里还有 isIssue（"true" / "false"，也可能给布尔）→ 该产品有没有问题
   - 后端一行 = 一个工位：既有工位位置信息（station_id / station_name /
     row_no / col_no / pos_no / station_status / station_type / operator /
     station_remark / group），也带该工位绑定的产品信息（serial_code /
     product_id / product_name / model / fsn / task_number …）
     → serial_code 为空 = 该工位没绑产品（空闲）；产品字段不发或为空 = 不显示
   - 布局：按 row_no 分行、col_no 分桌、pos_no 分位（有几行几列由数据决定），
     编号回退规则：station_id 缺失时按「区.行:02.列:02.位:02」兜底
   - 下钻单元：同一列上下紧挨的 2 行桌子为一组（与总览点击的那一组一一对应）
   - 工位颜色：未绑产品 idle 灰 / 执行状态 6 正常 normal 绿 /
     7 暂停 warning 黄 / 8 停工 error 红
   ============================================================ */
import { getStationList, getStationListSse } from '@/api/mainapi.js';

/** 两个页面当前对应的区（工位总览 / 工位详情都是 A 区）；传空 = 拉全部工位 */
export const ZONE_CODE = 'A';
/** 下钻单元：同一列上下紧挨的 2 行桌子为一组 */
export const PAIR_ROWS = 2;

/* ---------- 文案字典 ---------- */
/** 产品进程：只做展示（决定进度条） */
export const PROCESS_STATUS = { 
  1: '下达', 
  2: '装配中',
  3: '调试中',
  4: '检验中',
  5: '出库',
  6: '正常',
  7: '暂停',
  8: '停工',
  9: '入库中',
  10:'配料',
  11:'入库',
  12:'领料',
  13:'点料', };
/** 产品执行状态：决定工位颜色 */
export const EXECUTE_STATUS = { 1: '下达', 6: '正常', 7: '暂停', 8: '停工' };
/** 工位状态文案 */
export const STATUS_TXT = { normal: '正常', warning: '暂停', error: '停工', idle: '空闲' };

/* ---------- 工具函数 ---------- */
export const pad2 = n => String(n).padStart(2, '0');
/** 时间文案 HH:MM:SS（不传参取当前时间） */
export function nowTime(date) {
  const d = date || new Date();
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
}
export function processText(v) { return PROCESS_STATUS[v] || '—'; }
export function executeText(v) { return EXECUTE_STATUS[v] || '—'; }
/** 具体型号 → 系列（'PA600-RQDEx' → 'PA600'；取不到前缀时退回第一个 '-' 之前） */
export function seriesOf(model) {
  if (!model) return '';
  const hit = String(model).match(/^[A-Za-z]+\d+/);
  return hit ? hit[0] : String(model).split('-')[0];
}
/** 执行状态 → 工位状态（颜色）：7 暂停黄 / 8 停工红 / 其余在制绿 / 未绑产品灰 */
export function stationStatusOf(executeStatus, hasProduct) {
  if (!hasProduct) return 'idle';
  const v = Number(executeStatus);
  if (v === 7) return 'warning';
  if (v === 8) return 'error';
  return 'normal';
}

/** 有值判定：后端字段漏传 / 空串 / null 都算「没有」，详情页不显示 */
function has(v) {
  return v !== undefined && v !== null && v !== '';
}
/** 只取有值的字符串，没有就给空串（详情页据此决定显不显示这一项） */
function str(v) {
  return has(v) ? String(v) : '';
}
/** 「有无问题」：后端可能给布尔 true/false，也可能给字符串 "true"/"false"，统一转成布尔 */
function boolOf(v) {
  return v === true || v === 'true' || v === 1 || v === '1';
}

/**
 * 后端一行 → 前端工位对象
 * 工位位置信息 + 该工位绑定的产品（serial_code 为空 = 没绑产品 = 空闲）
 */
export function normalizeStation(raw) {
  const r = raw || {};
  const rowNo = Number(r.row_no) || 0;
  const colNo = Number(r.col_no) || 0;
  const posNo = Number(r.pos_no) || 0;
  const processCode = str(r.process_code) || ZONE_CODE;
  // 编号：优先用后端 station_id，缺了按「区.行.列.位」兜底
  const id = str(r.station_id) || `${processCode}.${pad2(rowNo)}.${pad2(colNo)}.${pad2(posNo)}`;
  const serialCode = str(r.serial_code);

  // 绑了产品才有产品信息：字段缺 / 空一律留空，详情页不显示
  const product = serialCode ? {
    id: r.product_id,                       // 产品ID
    serialCode,                             // 产品编号
    fsn: str(r.fsn),                        // 短代码
    name: str(r.product_name),              // 产品名称
    model: str(r.model),                    // 产品型号
    taskNumber: str(r.task_number),         // 任务单号
    contract: str(r.contract),              // 合同号
    contractNo: str(r.contract_no),         // 合同编号
    owner: str(r.endUser),                  // 最终业主
    range: str(r.range),                    // 量程
    designer: str(r.designer),              // 设计人
    replyDate: str(r.reply_date),           // 回复交货期
    planFinishDate: str(r.plan_finish_date),// 计划完成时间
    createBy: str(r.create_by),             // 创建人
    updateBy: str(r.update_by),             // 更新人
    createTime: str(r.create_time),         // 创建时间
    updateTime: str(r.update_time),         // 更新时间
    startTime: str(r.start_time),           // 开始时间
    detail: str(r.detail),                  // 备注
    remark: str(r.remark),                  // 小备注
    parameter: str(r.parameter),            // 调试参数
    processStatus: Number(r.process_status) || 0,   // 产品进程状态
    executeStatus: Number(r.execute_status) || 0,   // 产品执行状态
    serialCodeOriginal: str(r.serialCode_original), // 初始编号
    serialCodeHistory: str(r.serialCode_history),   // 历史编号
    isIssue: boolOf(r.isIssue),             // 有无问题（详情弹窗里具体的问题列表走另一个接口）
  } : null;

  return {
    id,
    short: pad2(posNo),             // 末两位位号（界面小图标只显示它）
    name: str(r.station_name),      // 工位名称
    rowNo,
    colNo,
    posNo,
    processCode,
    stationStatus: str(r.station_status), // 后端工位状态文案（如 生产中 / 待机）
    type: str(r.station_type),
    operator: str(r.operator),
    remark: str(r.station_remark),
    group: str(r.group),
    product,
    model: product ? product.model : '',   // 在制产品型号（卡片 / 统计 / 筛选用）
    status: stationStatusOf(r.execute_status, !!product),
    note: '',                       // 暂停 / 停工原因：接口未返回，不显示
    pairIndex: -1,                  // 所属下钻单元下标（buildBoard 回填）
  };
}

/**
 * 工位数组 → 看板结构（两个页面共用，保证「第 N 组」口径一致）
 * @param {Array} list 后端返回的工位数组
 * @returns {{ zone: string, rows: Array, pairs: Array, pairRows: Array<Array> }}
 *   zone     区号（取数据中的 process_code，取不到用 ZONE_CODE）
 *   rows     行结构（总览按行铺桌子）
 *   pairs    下钻单元（详情页按组取数）
 *   pairRows 单元按行分组（总览排版用）
 */
export function buildBoard(list) {
  const stations = (list || []).map(normalizeStation);
  const zone = (stations.find(s => s.processCode) || {}).processCode || ZONE_CODE;
  const rowNos = [...new Set(stations.map(s => s.rowNo))].sort((a, b) => a - b);
  const colNos = [...new Set(stations.map(s => s.colNo))].sort((a, b) => a - b);

  const rows = rowNos.map((rowNo) => {
    const rowCode = `${zone}.${pad2(rowNo)}`;
    const row = { rowNo, rowNoText: pad2(rowNo), rowCode, thin: false, tables: [] };
    row.tables = colNos.map((colNo) => {
      const table = {
        code: `${rowCode}.${pad2(colNo)}`,
        colNo,
        rowNo,
        rowCode,
        thin: false,
        stations: [],
        row, // 桌子回指所在行
      };
      // 同一桌的工位按位号从小到大横排（与现场工位顺序一致）
      table.stations = stations
        .filter(s => s.rowNo === rowNo && s.colNo === colNo)
        .sort((a, b) => a.posNo - b.posNo);
      table.thin = table.stations.length <= 2; // 2 工位桌（原第 7-10 行那种）排版更窄
      return table;
    });
    row.thin = row.tables.length > 0 && row.tables.every(t => t.thin);
    return row;
  });

  const pairs = [];
  const pairRows = [];
  for (let i = 0; i < rows.length; i += PAIR_ROWS) {
    const grpRows = rows.slice(i, i + PAIR_ROWS);
    const line = [];
    colNos.forEach((colNo, c) => {
      const tables = grpRows.map(r => r.tables[c]);
      const pair = {
        index: pairs.length,
        code: tables.map(t => t.code).join(' / '),   // A.01.03 / A.02.03
        rowCodes: grpRows.map(r => r.rowCode),
        rows: grpRows,
        tables,
        thin: grpRows[0].thin,
        stationCount: tables.reduce((n, t) => n + t.stations.length, 0),
      };
      // 工位回指所属单元：详情页按单元分流告警 / 入库中数
      tables.forEach(t => t.stations.forEach(s => { s.pairIndex = pair.index; }));
      pairs.push(pair);
      line.push(pair);
    });
    pairRows.push(line);
  }

  return { zone, rows, pairs, pairRows };
}

/**
 * 拉一个区的工位：processCode='A' → A 区全部工位；空 → 全部工位
 * @param {string} processCode 区号（默认 ZONE_CODE）
 * @returns {Promise<{zone, rows, pairs, pairRows}>}
 */
export async function fetchBoard(processCode = ZONE_CODE) {
  const res = await getStationList({ processCode: processCode || '' });
  const body = (res && res.data) || {};
  if (body.code !== 200) throw new Error(body.message || '返回数据格式异常');
  const list = Array.isArray(body.data) ? body.data : [];
  return buildBoard(list);
}

/** SSE 推来的一条消息 → 工位数组（后端可能直接给数组，也可能包成 { code, data }） */
function pickList(payload) {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return null;
  const d = payload.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d && d.data)) return d.data;   // { code, message, data: [] }
  if (Array.isArray(payload.stations)) return payload.stations;
  return null;
}

/**
 * 订阅工位信息 SSE：后端每 5 秒推一次本区最新工位，推来的都是全量快照，
 * 拿到后走和 fetchBoard 同一套 buildBoard，页面不用区分数据来源
 * @param {string} processCode 区号（默认 ZONE_CODE）
 * @param {{onBoard:Function, onError:Function}} handlers
 *   onBoard(board) 每收到一条快照就回调一次（board 结构与 fetchBoard 一致）
 *   onError(err)   流断了回调（EventSource 自己会重连，这里只做提示）
 * @returns {EventSource} 关掉就 .close()
 */
export function openStationStream(processCode = ZONE_CODE, handlers = {}) {
  return getStationListSse({ processCode: processCode || ZONE_CODE }, {
    onMessage: (ev) => {
      let payload;
      try {
        payload = JSON.parse(ev.data);
      } catch (e) {
        // 后端偶尔会推心跳（纯文本），解析不了就忽略
        if (handlers.onError) handlers.onError(new Error('SSE 消息不是 JSON：' + ev.data));
        return;
      }
      const list = pickList(payload);
      if (!list) return;   // 非工位数据（心跳 / 空推）不吃
      handlers.onBoard(buildBoard(list));
    },
    onError: handlers.onError,
  });
}
